import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { FormObjectType, JoinTermsAgreeItemsType } from 'src/types/react-hook-form'
import { TermsListSpace } from 'src/types/api'
import useParamsType from 'src/hooks/useParamsType.hook'

const useJoinTermsAgreeForm = (
  termsData: TermsListSpace.Response,
  options?: { telecom?: string },
) => {
  const params = useParamsType()
  const telecom = options?.telecom ?? params.telecom ?? ''
  const {
    Insurance,
    PrivacyThirdParty,
    PrivacyConsent,
    LGUPPrivacy,
    Service,
    Marketing,
    KTPrivacy,
    SKTPrivacy,
  } = termsData.Terms

  const methods = useForm<JoinTermsAgreeItemsType>({
    mode: 'onChange',
    defaultValues: {
      PrivacyConsent: false,
      Service: false,
      Marketing: false,
      allTerms: false,
      Insurance: false,
      LGUPPrivacy: false,
      PrivacyThirdParty: false,
      KTPrivacy: false,
      SKTPrivacy: false,
    },
  })
  const {
    getValues,
    formState: { isValid },
    setValue,
    trigger,
  } = methods

  /** 개별 필수 약관만 모두 체크해도 `[필수] 서비스 필수 약관` 행이 맞게 체크되도록 동기화 */
  useEffect(() => {
    setValue('allTerms', isValid, { shouldDirty: true, shouldValidate: false })
  }, [isValid, setValue])

  const formatTextWithRequirement = (text?: string, isRequire?: 0 | 1) => {
    return !!text ? `${text}(${isRequire === 1 ? '필수' : '선택'})` : undefined
  }

  //필수 약관 동의 요소 추출
  const isRequiredTermsList = () => {
    return Object.entries(termsData?.Terms || {}).reduce(
      (isRequiredList: string[], [key, value]) => {
        if (typeof value === 'object' && value.Required === 1) {
          isRequiredList.push(`${key}`)
        }
        return isRequiredList
      },
      [],
    )
  }

  // 필수약관 모두 동의
  const allTermsAgreeHandler = () => {
    const values = getValues()
    const requiredTermsList = isRequiredTermsList()

    for (const key of Object.keys(values)) {
      if (requiredTermsList.includes(key)) {
        setValue(key as keyof JoinTermsAgreeItemsType, !isValid)
      }
    }

    setValue('allTerms', !getValues('allTerms'))
  }

  const registerAttribute: Record<keyof JoinTermsAgreeItemsType, Partial<FormObjectType>> = {
    allTerms: {
      tag: 'allTerms',
      register_options: { required: false, onClick: allTermsAgreeHandler },
      labelValue: '[필수] 서비스 필수 약관',
    },
    Service: {
      tag: 'Service',
      register_options: { required: true },
      labelValue: formatTextWithRequirement(Service?.Title, Service?.Required),
      modalData: { t: Service?.Title, children: Service?.Contents },
    },
    PrivacyConsent: {
      tag: 'PrivacyConsent',
      register_options: { required: true },
      labelValue: formatTextWithRequirement(PrivacyConsent?.Title, PrivacyConsent?.Required),
      modalData: { t: PrivacyConsent?.Title, children: PrivacyConsent?.Contents },
    },
    SKTPrivacy: {
      tag: 'SKTPrivacy',
      register_options: { required: telecom === '0' },
      labelValue: formatTextWithRequirement(SKTPrivacy?.Title, SKTPrivacy?.Required),
      modalData: { t: SKTPrivacy?.Title, children: SKTPrivacy?.Contents },
    },
    KTPrivacy: {
      tag: 'KTPrivacy',
      register_options: { required: telecom === '1' },
      labelValue: formatTextWithRequirement(KTPrivacy?.Title, KTPrivacy?.Required),
      modalData: { t: KTPrivacy?.Title, children: KTPrivacy?.Contents },
    },
    LGUPPrivacy: {
      tag: 'LGUPPrivacy',
      register_options: { required: telecom === '2' },
      labelValue: formatTextWithRequirement(LGUPPrivacy?.Title, LGUPPrivacy?.Required),
      modalData: { t: LGUPPrivacy?.Title, children: LGUPPrivacy?.Contents },
    },
    PrivacyThirdParty: {
      tag: 'PrivacyThirdParty',
      register_options: { required: true },
      labelValue: formatTextWithRequirement(PrivacyThirdParty?.Title, PrivacyThirdParty?.Required),
      modalData: { t: PrivacyThirdParty?.Title, children: PrivacyThirdParty?.Contents },
    },
    Marketing: {
      tag: 'Marketing',
      register_options: { required: false },
      labelValue: formatTextWithRequirement(Marketing?.Title, Marketing?.Required),
      modalData: { t: Marketing?.Title, children: Marketing?.Contents },
    },
    Insurance: {
      tag: 'Insurance',
      register_options: { required: false },
      labelValue: formatTextWithRequirement(Insurance?.Title, Insurance?.Required),
      modalData: { t: Insurance?.Title, children: Insurance?.Contents },
    },
  }

  /** 필수 약관 + 전체 동의 체크(에러 모달 등에서 호출). 선택(Marketing·Insurance)은 건드리지 않음. */
  const agreeAllRequiredTerms = () => {
    ;(Object.keys(registerAttribute) as (keyof JoinTermsAgreeItemsType)[]).forEach((key) => {
      if (key === 'allTerms') return
      const attr = registerAttribute[key]
      if (attr?.register_options?.required === true) {
        setValue(key, true, { shouldDirty: true, shouldValidate: true })
      }
    })
    setValue('allTerms', true, { shouldDirty: true, shouldValidate: true })
    void trigger()
  }

  return {
    methods,
    registerAttribute,
    agreeAllRequiredTerms,
  }
}
export default useJoinTermsAgreeForm
